import {
  alpha,
  Box,
  styled,
  Table,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { CSSProperties } from 'react';

interface StyledTableCellProps {
  index?: number;
  isHeaderCell?: boolean;
  lockFirstColumn?: boolean;
  customWidth?: CSSProperties['width'];
  showColumnSeparators?: boolean;
}

export const StyledTableBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  gap: 0,
  border: `1px solid ${theme.hhglobal.neutral.gray300}`,
  borderRadius: theme.hhglobal.spacing.borderRadius[1].valuePx,
  overflow: 'hidden',
  width: '100%',
}));

export const StyledTableOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.hhglobal.spacing.spacing[4].valuePx,
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  cursor: 'progress',
}));

export const StyledTable = styled(Table)(() => ({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
}));

export const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'lockFirstColumn' && prop !== 'index' && prop !== 'isHeaderCell' && prop !== 'customWidth' && prop !== 'showColumnSeparators',
})<StyledTableCellProps>(({ theme, lockFirstColumn, index, isHeaderCell, customWidth, showColumnSeparators }) => ({
  maxWidth: !lockFirstColumn ? `${customWidth}px` : 'auto',
  ...(showColumnSeparators && {
    borderRight: `1px solid ${theme.hhglobal.neutral.gray300}`,
    '&:last-child': {
      borderRight: 'none',
    },
  }),
  ...(lockFirstColumn &&
    index === 0 && {
      position: 'sticky',
      left: 0,
      zIndex: 5,
      width: customWidth ? `${customWidth}px` : undefined,
      backgroundColor: isHeaderCell ? theme.hhglobal.neutral.gray100 : theme.palette.background.paper,
      whiteSpace: 'nowrap',
      '&::after': {
        content: '""',
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        width: '1px',
        backgroundColor: theme.hhglobal.neutral.gray300,
        zIndex: 4,
      },
    }),
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.hhglobal.neutral.gray100,
    color: theme.hhglobal.neutral.superblack,
    fontSize: theme.hhglobal.typography.body.large.fontSize,
    fontWeight: theme.hhglobal.typography.fontWeights.bold,
    fontFamily: theme.hhglobal.typography.typefaces.primary,
    padding: `0 ${theme.hhglobal.spacing.spacing[4].valuePx}`,
    height: theme.hhglobal.spacing.spacing[8].valuePx,
    lineHeight: theme.hhglobal.typography.body.large.lineHeight,
    whiteSpace: 'nowrap',
    position: 'sticky',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: theme.hhglobal.typography.body.base.fontSize,
    fontWeight: theme.hhglobal.typography.fontWeights.regular,
    lineHeight: theme.hhglobal.typography.body.base.lineHeight,
  },
}));

export const StyledTableContainer = styled(TableContainer)(() => ({
  boxShadow: 'none',
  maxWidth: '100%',
  minHeight: 0,
  maxHeight: '60dvh',
  overflowX: 'auto',
})) as typeof TableContainer;

export const StyledTableHead = styled(TableHead)({
  position: 'sticky',
  left: 0,
  top: 0,
  zIndex: 99,
  width: '100%',
});

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'hoverable',
})<{ hoverable?: boolean }>(({ theme, hoverable = true }) => ({
  ...(hoverable && {
    '&:hover': {
      backgroundColor: theme.hhglobal.purpose.fillWeak,
    },
  }),
}));

export const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  borderTop: `1px solid ${theme.hhglobal.neutral.gray300}`,
  fontSize: theme.hhglobal.typography.body.base.fontSize,
  ['& .MuiTablePagination-selectLabel']: {
    fontSize: theme.hhglobal.typography.body.base.fontSize,
  },
  ['& .MuiTablePagination-displayedRows']: {
    fontSize: theme.hhglobal.typography.body.base.fontSize,
    color: theme.hhglobal.neutral.superblack,
  },
})) as typeof TablePagination;
